import { VoteOption, VotingStats } from '../../hooks/useVoting'
import { useComposeCast } from '@coinbase/onchainkit/minikit'

interface ShareButtonProps {
  stats: VotingStats | null
  userVote: VoteOption | null
}

export default function ShareButton({ stats, userVote }: ShareButtonProps) {
  const { composeCast } = useComposeCast()

  // Don't render if no stats available
  if (!stats || stats.totalVotes === BigInt(0)) {
    return null
  }

  const handleShare = async () => {
    // Calculate percentages
    const total = Number(stats.totalVotes)
    const positive = Number(stats.positiveVotes)
    const negative = Number(stats.negativeVotes) // Assuming negativeVotes exists on stats based on previous context, user code had it.

    const positivePct = total > 0 ? Math.round((positive / total) * 100) : 0
    const negativePct = total > 0 ? Math.round((negative / total) * 100) : 0

    let castText = ''

    if (userVote !== null) {
      // "Degen Pulse" for users who have voted
      const isBullish = userVote === VoteOption.Positive
      const emoji = isBullish ? '🐂' : '🐻'
      const sentiment = isBullish ? 'Bullish AF' : 'Bearish AF'

      castText = `Daily vibe check on Base: ${sentiment} ${emoji}\nVerify the vibes here 👇\n\n#BaseVibe`
    } else {
      // "Alpha Leaker" for generic/community stats
      // Determine dominant sentiment
      const isBullish = positive >= negative
      const emoji = isBullish ? '🐂' : '🐻'
      const sentiment = isBullish ? 'Bullish' : 'Bearish'
      const percentage = isBullish ? positivePct : negativePct

      // "Vibe Check: 68% of Base is Bullish today..."
      castText = `Vibe Check: ${percentage}% of Base is ${sentiment} today ${emoji}.\nAre we top signaling or just getting started?\nSee the real-time sentiment 👇 #BaseVibe`
    }

    // Try to cast to Base/Farcaster
    try {
      composeCast({
        text: castText,
        embeds: [window.location.href],
      })
    } catch (error) {
      console.error('Error casting:', error)
      // Fallback: Copy to clipboard (for development/testing outside Base app)
      try {
        await navigator.clipboard.writeText(castText)
        alert('Cast text copied! When running in Base app, this will open the composer automatically.')
      } catch (clipboardError) {
        console.error('Error copying to clipboard:', clipboardError)
        alert(`Cast text:\n\n${castText}`)
      }
    }
  }

  return (
    <button
      onClick={handleShare}
      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium text-sm"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
        />
      </svg>
      Share Results
    </button>
  )
}

